import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { MapPin, Heart, Calendar, Search, Filter, Package } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/lib/supabase";
import { usePlacesAutocomplete } from "@/hooks/usePlacesAutocomplete";
import { ChevronDown, ChevronUp, Check } from "lucide-react";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
} from "@/components/ui/command";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";

import { VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY } from "@/utils/const";
import { AuthContextType } from "@/types/authContext";

interface Doula {
	id: string;
	name: string;
	title: string;
	location: string;
	distance: number | string;
	experience_years: number;
	package_count: number;
	specialties: string[];
	bio: string;
	profile_image: string;
	is_favorited: boolean;
	is_verified?: boolean;
	tags?: string[];
	personality_traits?: string[];
}

// Filter options
const additionalSpecialtiesOptions = [
	"International Board Certified Lactation Consultant (IBCLC - 1000+ hour certification)",
	"Lactation Specialist/Counselor (50+ hour Certification)",
	"Massage/Bodyworker",
	"Yoga Teacher",
	"Belly binding",
	"Rebozo",
	"Spinning Babies",
	"Hypnobirthing",
	"Birth Photography",
	"Unmedicated Births",
	"Postpartum Chef",
	"Sleep consultant",
	"Childbirth Educator",
];

const genderOptions = ["Female", "Male", "Non-Binary", "Transgender"];
const sexualityOptions = ["Queer", "Poly", "Heterosexual", "Prefer not to say"];
const raceOptions = [
	"Caucasian",
	"Black",
	"Hispanic",
	"Native American",
	"Pacific Islander",
	"South Asian",
	"East Asian",
	"Prefer not to say",
];
const religionOptions = [
	"Christian",
	"Muslim",
	"Hindu",
	"Jewish",
	"Buddhist",
	"Prefer not to say",
];

const styleOptions = [
	"practical",
	"spiritual",
	"mindful",
	"warm",
	"assertive",
	"humorous",
	"holistic",
	"open-minded",
	"direct",
	"earthy",
	"affirming",
	"empowering",
	"motivational",
	"compassionate",
	"creative",
	"analytical",
	"energetic",
	"encouraging",
];

const doulaServiceTypeOptions = [
	"Birth Doula",
	"Daytime Postpartum Doula",
	"Overnight Postpartum Doula",
];

const languagesOptions = [
	"English",
	"Spanish",
	"Albanian",
	"American Sign Language",
	"Arabic",
	"Armenian",
	"Assamese",
	"Bengali",
	"Bosnian",
	"Bulgarian",
	"Cantonese",
	"Croatian",
	"Danish",
	"Dutch",
	"Farsi",
	"Flemish",
	"French",
	"Fuzhounese",
	"German",
	"Greek",
	"Gujarati",
	"Haitian Creole",
	"Hebrew",
	"Hindi",
	"Hmong",
	"Hungarian",
	"Icelandic",
	"Igbo",
	"Italian",
	"Japanese",
	"Kashmiri",
	"Korean",
	"Kurdish",
	"Malayalam",
	"Mandarin",
	"Marathi",
	"Norwegian",
	"Polish",
	"Portuguese",
	"Punjabi",
	"Romanian",
	"Russian",
	"Serbian",
	"Shona",
	"Somali",
	"Swahili",
	"Tagalog",
	"Thai",
	"Turkish",
	"Twi",
	"Ukrainian",
	"Urdu",
	"Vietnamese",
	"Yiddish",
	"Yoruba",
];

const birthsAttendedOptions = [
	"1-9",
	"10-19",
	"20-49",
	"50-74",
	"75-99",
	"100-199",
	"200-499",
	"500-1000",
	"1000+",
];
const costRangeOptions = [
	"$0 - $1000",
	"$1000-$1500",
	"$1500-$2000",
	"$2000-$2500",
	"$2500-$3000",
	"$3000-$3500",
	"$3500-$4000",
	"$4000+",
];

// Multi-select dropdown component
interface MultiSelectDropdownProps {
	label: string;
	options: string[];
	selectedValues: string[];
	onToggle: (value: string) => void;
	placeholder?: string;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
	label,
	options,
	selectedValues,
	onToggle,
	placeholder = "Select...",
}) => {
	const [open, setOpen] = useState(false);

	// Create display text for selected values
	const getDisplayText = () => {
		if (selectedValues.length === 0) return placeholder;
		if (selectedValues.length === 1) {
			// Show full text for single selection, truncate if too long
			const text = selectedValues[0];
			return text.length > 25 ? `${text.substring(0, 25)}...` : text;
		}
		return `${selectedValues.length} selected`;
	};

	return (
		<div className="space-y-1">
			<label className="text-xs font-medium text-gray-700 font-karla block">
				{label}
			</label>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<Button
						variant="outline"
						role="combobox"
						aria-expanded={open}
						className="w-full justify-between h-10 bg-white border-gray-300 text-sm px-3 min-w-0"
						title={
							selectedValues.length === 1
								? selectedValues[0]
								: `${selectedValues.length} items selected`
						}
					>
						<span className="flex-1 text-left overflow-hidden">
							{getDisplayText()}
						</span>
						<ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
					</Button>
				</PopoverTrigger>
				<PopoverContent
					className="w-80 p-0 bg-white border border-gray-200 shadow-lg"
					align="start"
				>
					<Command className="bg-white">
						<CommandInput
							placeholder={`Search ${label.toLowerCase()}...`}
							className="bg-white"
						/>
						<CommandEmpty>No {label.toLowerCase()} found.</CommandEmpty>
						<CommandGroup className="max-h-64 overflow-y-auto">
							{options.map((option) => (
								<CommandItem
									key={option}
									value={option}
									onSelect={() => onToggle(option)}
									className="cursor-pointer hover:bg-gray-50 text-sm py-2 px-3"
								>
									<Check
										className={`mr-2 h-4 w-4 shrink-0 ${selectedValues.includes(option)
											? "opacity-100 text-primary-600"
											: "opacity-0"
											}`}
									/>
									<span className="flex-1 whitespace-normal break-words leading-tight">
										{option}
									</span>
								</CommandItem>
							))}
						</CommandGroup>
					</Command>
				</PopoverContent>
			</Popover>
		</div>
	);
};

const SearchDoulas: React.FC = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const { user, profile } = useAuth() as AuthContextType;

	// Get search data from landing page or sessionStorage
	const searchDataFromState = location.state?.searchData;
	const searchDataFromStorage = sessionStorage.getItem("parentSearchData");

	// If user is logged in and has a parent profile, use their provider
	const getInitialProvider = () => {
		// If logged in as parent, use their provider name
		if (user && profile?.role === "parent" && (profile as any).provider_name) {
			return (profile as any).provider_name;
		}

		// Otherwise use search data from landing page or storage
		return (
			searchDataFromState?.providerName ||
			(searchDataFromStorage
				? JSON.parse(searchDataFromStorage).providerName
				: "")
		);
	};

	const getInitialDueDate = () => {
		// If logged in as parent, use their due date
		if (user && profile?.role === "parent" && (profile as any).due_date) {
			return (profile as any).due_date;
		}

		// Otherwise use search data from landing page or storage
		return (
			searchDataFromState?.dueDate ||
			(searchDataFromStorage ? JSON.parse(searchDataFromStorage).dueDate : "")
		);
	};

	const [searchProvider, setSearchProvider] = useState(getInitialProvider());
	const [dueDate, setDueDate] = useState(getInitialDueDate());
	const [distance, setDistance] = useState("25");
	const [doulas, setDoulas] = useState<Doula[]>([]);
	const [loading, setLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [favorites, setFavorites] = useState<Set<string>>(new Set());
	const [hasMore, setHasMore] = useState(true);
	const [offset, setOffset] = useState(0);

	// Filter states
	const [showFilters, setShowFilters] = useState(false);

	// Filter states - all now support multi-select
	const [filters, setFilters] = useState({
		additionalSpecialties: [] as string[],
		gender: [] as string[],
		sexuality: [] as string[],
		race: [] as string[],
		religion: [] as string[],
		style: [] as string[],
		doulaServiceType: [] as string[],
		languagesSpoken: [] as string[],
		birthsAttended: [] as string[],
		costRange: [] as string[],
	});

	const searchProviderRef = useRef<HTMLInputElement>(null);
	const { place: searchProviderPlace } = usePlacesAutocomplete({
		inputRef: searchProviderRef,
		types: ["health"],
		componentRestrictions: { country: "us" },
		fields: ["place_id", "formatted_address", "name", "types", "geometry", "address_components"],
	});

	useEffect(() => {
		if (!searchProviderPlace?.name) return;
		setSearchProvider(searchProviderPlace.name);
	}, [searchProviderPlace]);

	// Update provider and due date when user profile changes
	useEffect(() => {
		if (user && profile?.role === "parent") {
			if ((profile as any).provider_name && !searchProvider) {
				setSearchProvider((profile as any).provider_name);
			}
			if ((profile as any).due_date && !dueDate) {
				setDueDate((profile as any).due_date);
			}
		}
	}, [user, profile, searchProvider, dueDate]);

	// Function to fetch doulas from edge function
	const fetchDoulas = async (
		provider: string,
		distance: string,
		loadMore: boolean = false,
	) => {
		if (loadMore) {
			setLoadingMore(true);
		} else {
			setLoading(true);
			setOffset(0); // Reset offset for new search
			setHasMore(true); // Reset hasMore for new search
		}

		try {
			const currentOffset = loadMore ? offset : 0;
			const params = new URLSearchParams({
				provider: provider,
				distance: distance,
				limit: "24", // Increase to 24 for better grid layout
				offset: currentOffset.toString(),
			});

			// Add due date for month filtering if available
			if (dueDate) {
				params.append("due_date", dueDate);
			}

			// Add filters if they exist - all now sent as JSON arrays
			if (filters.additionalSpecialties.length > 0) {
				params.append(
					"additional_specialties",
					JSON.stringify(filters.additionalSpecialties),
				);
			}
			if (filters.gender.length > 0) {
				params.append("gender", JSON.stringify(filters.gender));
			}
			if (filters.sexuality.length > 0) {
				params.append("sexuality", JSON.stringify(filters.sexuality));
			}
			if (filters.race.length > 0) {
				params.append("race", JSON.stringify(filters.race));
			}
			if (filters.religion.length > 0) {
				params.append("religion", JSON.stringify(filters.religion));
			}
			if (filters.style.length > 0) {
				params.append("style", JSON.stringify(filters.style));
			}
			if (filters.doulaServiceType.length > 0) {
				params.append(
					"doula_service_type",
					JSON.stringify(filters.doulaServiceType),
				);
			}
			if (filters.languagesSpoken.length > 0) {
				params.append(
					"languages_spoken",
					JSON.stringify(filters.languagesSpoken),
				);
			}
			if (filters.birthsAttended.length > 0) {
				params.append(
					"births_attended",
					JSON.stringify(filters.birthsAttended),
				);
			}
			if (filters.costRange.length > 0) {
				params.append("cost_range", JSON.stringify(filters.costRange));
			}

			const response = await fetch(
				`${VITE_SUPABASE_URL}/functions/v1/search-doulas?${params}`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${VITE_SUPABASE_ANON_KEY}`,
					},
				},
			);

			const result = await response.json();

			if (!response.ok) {
				console.error("Error fetching doulas:", result.error);
				// Show empty state if edge function fails
				setDoulas([]);
				return;
			}

			const doulasData = result.data || [];

			if (loadMore) {
				// Append new doulas for infinite scroll
				setDoulas((prev) => [...prev, ...doulasData]);
				setOffset((prev) => prev + doulasData.length);
			} else {
				// Replace doulas for new search
				setDoulas(doulasData);
				setOffset(doulasData.length);
			}

			// Check if there are more results from API response
			setHasMore(result.pagination?.has_more || false);

			// Update favorites state based on the data
			const favoritedIds = new Set<string>();
			const allDoulas = loadMore ? [...doulas, ...doulasData] : doulasData;
			allDoulas.forEach((doula: Doula) => {
				if (doula.is_favorited) {
					favoritedIds.add(doula.id);
				}
			});
			setFavorites(favoritedIds);
		} catch (error) {
			console.error("Error fetching doulas:", error);
			// Show empty state if there's an error
			setDoulas([]);
		} finally {
			setLoading(false);
			setLoadingMore(false);
		}
	};

	useEffect(() => {
		// If we have search data, automatically search
		if (searchProvider) {
			fetchDoulas(searchProvider, distance);
		} else {
			// Don't show any doulas if no search provider
			setDoulas([]);
		}
	}, [searchProvider, distance]); // Re-run when searchProvider or distance changes


	const handleSearch = () => {
		fetchDoulas(searchProvider, distance, false);
	};

	const loadMoreDoulas = () => {
		if (!loadingMore && hasMore) {
			fetchDoulas(searchProvider, distance, true);
		}
	};

	// Filter helper functions - all updated for multi-select arrays
	const toggleArrayFilter = (filterType: string, value: string) => {
		const currentArray = filters[
			filterType as keyof typeof filters
		] as string[];
		const newArray = currentArray.includes(value)
			? currentArray.filter((item) => item !== value)
			: [...currentArray, value];

		setFilters((prev) => ({
			...prev,
			[filterType]: newArray,
		}));
	};

	const clearAllFilters = () => {
		setFilters({
			additionalSpecialties: [],
			gender: [],
			sexuality: [],
			race: [],
			religion: [],
			style: [],
			doulaServiceType: [],
			languagesSpoken: [],
			birthsAttended: [],
			costRange: [],
		});
	};

	const hasActiveFilters = () => {
		return (
			filters.additionalSpecialties.length > 0 ||
			filters.gender.length > 0 ||
			filters.sexuality.length > 0 ||
			filters.race.length > 0 ||
			filters.religion.length > 0 ||
			filters.style.length > 0 ||
			filters.doulaServiceType.length > 0 ||
			filters.languagesSpoken.length > 0 ||
			filters.birthsAttended.length > 0 ||
			filters.costRange.length > 0
		);
	};

	const getActiveFilterCount = () => {
		return Object.values(filters).reduce(
			(count: number, filterArray: string[]) => {
				return count + filterArray.length;
			},
			0,
		);
	};

	const toggleFavorite = async (doulaId: string) => {
		if (!user) {
			// If not logged in, redirect to parent signup
			sessionStorage.setItem("selectedDoulaId", doulaId);
			sessionStorage.setItem(
				"parentSearchData",
				JSON.stringify({
					providerName: searchProvider,
					dueDate: dueDate,
				}),
			);

			navigate("/parent-signup", {
				state: {
					searchData: { providerName: searchProvider, dueDate: dueDate },
					redirectTo: "/search-doulas",
					action: "favorite",
				},
			});
			return;
		}

		try {
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/toggle-favorite`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						doula_listing_id: doulaId,
					}),
				},
			);

			const result = await response.json();

			if (response.ok) {
				// Update local state
				const newFavorites = new Set(favorites);
				if (result.action === "added") {
					newFavorites.add(doulaId);
				} else {
					newFavorites.delete(doulaId);
				}
				setFavorites(newFavorites);

				// Update the doulas state to reflect the change
				setDoulas((prevDoulas) =>
					prevDoulas.map((doula) =>
						doula.id === doulaId
							? { ...doula, is_favorited: result.action === "added" }
							: doula,
					),
				);
			} else {
				console.error("Error toggling favorite:", result.error);
			}
		} catch (error) {
			console.error("Error toggling favorite:", error);
		}
	};

	const handleViewProfile = (doulaId: string) => {
		// Navigate to doula profile page
		navigate(`/doula-profile/${doulaId}`);
	};

	const handleBookAppointment = (doulaId: string) => {
		if (!user) {
			// If not logged in, redirect to parent signup
			sessionStorage.setItem("selectedDoulaId", doulaId);
			sessionStorage.setItem(
				"parentSearchData",
				JSON.stringify({
					providerName: searchProvider,
					dueDate: dueDate,
				}),
			);

			navigate("/parent-signup", {
				state: {
					searchData: { providerName: searchProvider, dueDate: dueDate },
					redirectTo: "/search-doulas",
					action: "book",
				},
			});
			return;
		}

		// If logged in, navigate to the doula profile page
		navigate(`/doula-profile/${doulaId}`);
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-gray-100">
			{/* Hero Section */}
			<div className="bg-white/80 backdrop-blur-md border-b border-gray-200 shadow-sm">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6 }}
						className="text-center"
					>
						<h1 className="font-normal text-black font-abc-prophet text-4xl leading-tight lg:text-[58px] lg:leading-[76px] mb-4">
							Find Your Perfect Doula
						</h1>
						<p className="text-xl lg:text-[24px] text-black font-karla mb-8">
							Connect with certified doulas in your area
						</p>

						{/* Search Form - Full Width */}
						<div className="max-w-6xl mx-auto">
							<Card className="glass-card border-0 shadow-lg">
								<CardContent className="p-6">
									<div className="flex flex-col lg:flex-row gap-4">
										<div className="flex-1">
											<div className="relative">
												<MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
												<Input
													ref={searchProviderRef}
													id="search-provider-input"
													placeholder="Search for your OBGYN/Midwife provider..."
													value={searchProvider}
													onChange={(e) => setSearchProvider(e.target.value)}
													className="pl-10 h-12 text-lg"
												/>
											</div>
										</div>
										<div className="w-full lg:w-48">
											<Select value={distance} onValueChange={setDistance}>
												<SelectTrigger className="h-12 text-lg bg-white border border-gray-300 shadow-sm">
													<SelectValue />
												</SelectTrigger>
												<SelectContent className="bg-white border border-gray-200 shadow-lg">
													<SelectItem value="10">Within 10 miles</SelectItem>
													<SelectItem value="25">Within 25 miles</SelectItem>
													<SelectItem value="50">Within 50 miles</SelectItem>
													<SelectItem value="100">Within 100 miles</SelectItem>
												</SelectContent>
											</Select>
										</div>
										<div className="w-full lg:w-64">
											<Select
												value={
													filters.doulaServiceType.length > 0
														? filters.doulaServiceType[0]
														: "all"
												}
												onValueChange={(value) => {
													if (value === "all") {
														setFilters((prev) => ({
															...prev,
															doulaServiceType: [],
														}));
													} else {
														setFilters((prev) => ({
															...prev,
															doulaServiceType: [value],
														}));
													}
												}}
											>
												<SelectTrigger className="h-12 text-lg bg-white border border-gray-300 shadow-sm">
													<SelectValue placeholder="Service Type" />
												</SelectTrigger>
												<SelectContent className="bg-white border border-gray-200 shadow-lg">
													<SelectItem value="all">All Service Types</SelectItem>
													<SelectItem value="Birth Doula">
														Birth Doula
													</SelectItem>
													<SelectItem value="Daytime Postpartum Doula">
														Daytime Postpartum Doula
													</SelectItem>
													<SelectItem value="Overnight Postpartum Doula">
														Overnight Postpartum Doula
													</SelectItem>
												</SelectContent>
											</Select>
										</div>
										<Button
											onClick={handleSearch}
											disabled={loading}
											className="h-12 px-8 bg-newTheme-lime hover:bg-newTheme-orange text-black shadow-btn-shadow"
										>
											{loading ? (
												<div className="flex items-center">
													<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
													Searching...
												</div>
											) : (
												<div className="flex items-center">
													<Search className="h-5 w-5 mr-2" />
													Find Doulas
												</div>
											)}
										</Button>
									</div>
								</CardContent>
							</Card>
						</div>
					</motion.div>
				</div>
			</div>

			{/* Filter Section - Full Width Collapsible */}
			<div className="w-full px-4 sm:px-6 lg:px-8 py-6 bg-gray-50">
				<div className="max-w-7xl mx-auto">
					<Card className="bg-white border-0 shadow-lg">
						<CardContent className="p-6">
							<Collapsible open={showFilters} onOpenChange={setShowFilters}>
								<CollapsibleTrigger asChild>
									<div className="flex justify-between items-center cursor-pointer">
										<div className="flex items-center gap-2">
											<Filter className="h-4 w-4 text-gray-600" />
											<h3 className="text-lg font-semibold text-gray-900 font-karla">
												Refine Your Search
											</h3>
											{hasActiveFilters() && (
												<Badge
													variant="secondary"
													className="ml-2 bg-primary-100 text-primary-800"
												>
													{getActiveFilterCount()} active
												</Badge>
											)}
										</div>
										<div className="flex items-center gap-2">
											{hasActiveFilters() && (
												<Button
													variant="ghost"
													size="sm"
													onClick={(e) => {
														e.stopPropagation();
														clearAllFilters();
													}}
													className="text-primary-600 hover:text-primary-700"
												>
													Clear All
												</Button>
											)}
											<Button
												variant="ghost"
												size="sm"
												className="text-gray-500"
											>
												{showFilters ? (
													<ChevronUp className="h-4 w-4" />
												) : (
													<ChevronDown className="h-4 w-4" />
												)}
											</Button>
										</div>
									</div>
								</CollapsibleTrigger>

								<CollapsibleContent className="mt-6">
									<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-4">
										{/* Specialties */}
										<MultiSelectDropdown
											label="Specialties"
											options={additionalSpecialtiesOptions}
											selectedValues={filters.additionalSpecialties}
											onToggle={(value) =>
												toggleArrayFilter("additionalSpecialties", value)
											}
											placeholder="Select specialties..."
										/>

										{/* Gender */}
										<MultiSelectDropdown
											label="Gender"
											options={genderOptions}
											selectedValues={filters.gender}
											onToggle={(value) => toggleArrayFilter("gender", value)}
											placeholder="Select gender..."
										/>

										{/* Race/Ethnicity */}
										<MultiSelectDropdown
											label="Race/Ethnicity"
											options={raceOptions}
											selectedValues={filters.race}
											onToggle={(value) => toggleArrayFilter("race", value)}
											placeholder="Select race..."
										/>

										{/* Languages */}
										<MultiSelectDropdown
											label="Languages"
											options={languagesOptions}
											selectedValues={filters.languagesSpoken}
											onToggle={(value) =>
												toggleArrayFilter("languagesSpoken", value)
											}
											placeholder="Select languages..."
										/>

										{/* Births Attended */}
										<MultiSelectDropdown
											label="Births Attended"
											options={birthsAttendedOptions}
											selectedValues={filters.birthsAttended}
											onToggle={(value) =>
												toggleArrayFilter("birthsAttended", value)
											}
											placeholder="Select range..."
										/>

										{/* Price Range */}
										<MultiSelectDropdown
											label="Price Range"
											options={costRangeOptions}
											selectedValues={filters.costRange}
											onToggle={(value) =>
												toggleArrayFilter("costRange", value)
											}
											placeholder="Select price..."
										/>

										{/* Style */}
										<MultiSelectDropdown
											label="Doula Style"
											options={styleOptions}
											selectedValues={filters.style}
											onToggle={(value) => toggleArrayFilter("style", value)}
											placeholder="Select styles..."
										/>

										{/* Religion */}
										<MultiSelectDropdown
											label="Religion"
											options={religionOptions}
											selectedValues={filters.religion}
											onToggle={(value) => toggleArrayFilter("religion", value)}
											placeholder="Select religion..."
										/>

										{/* Sexuality */}
										<MultiSelectDropdown
											label="Sexuality"
											options={sexualityOptions}
											selectedValues={filters.sexuality}
											onToggle={(value) =>
												toggleArrayFilter("sexuality", value)
											}
											placeholder="Select sexuality..."
										/>
									</div>

									{hasActiveFilters() && (
										<div className="mt-6 flex justify-center">
											<Button
												onClick={handleSearch}
												disabled={loading}
												className="px-8 bg-primary-500 hover:bg-primary-600 text-white"
											>
												Apply Filters
											</Button>
										</div>
									)}
								</CollapsibleContent>
							</Collapsible>
						</CardContent>
					</Card>
				</div>
			</div>

			{/* Results Section */}
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
				<div className="mb-8">
					<h2 className="text-2xl font-bold text-gray-900 font-karla mb-2">
						Available Doulas
					</h2>
					<p className="text-gray-600 font-karla">
						{doulas.length} doulas found in your area
					</p>
				</div>

				{doulas.length > 0 && (
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
						{doulas.map((doula, index) => (
							<motion.div
								key={doula.id}
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6, delay: index * 0.1 }}
							>
								<Card
									className={`glass-card hover:shadow-2xl transition-all duration-300 border border-gray-100 shadow-lg ${doula.is_favorited
										? "ring-2 ring-primary-200 bg-primary-50/30"
										: ""
										}`}
								>
									<div className="pb-4 pt-6 px-6">
										<div className="flex items-start justify-between">
											<div className="flex items-start space-x-4">
												{/* Enhanced Profile Image */}
												<div className="w-20 h-20 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0 shadow-md border border-gray-200">
													<img
														src={doula.profile_image}
														alt={doula.name}
														className="w-full h-full object-cover"
														onError={(e) => {
															e.currentTarget.src = "/placeholder.svg";
														}}
													/>
												</div>
												<div className="flex-1 min-w-0">
													{/* Name */}
													<div className="flex items-center space-x-2 mb-1">
														<h3 className="text-xl font-bold text-gray-900 font-karla truncate">
															{doula.name}
														</h3>
														{doula.is_verified && (
															<Badge
																variant="secondary"
																className="text-xs bg-green-100 text-green-800"
															>
																Verified
															</Badge>
														)}
														{doula.is_favorited && (
															<Badge
																variant="secondary"
																className="text-xs bg-primary-100 text-primary-800"
															>
																❤️ Favorited
															</Badge>
														)}
													</div>
													{/* Title */}
													<p className="text-sm text-gray-600 font-karla mb-1">
														{doula.title}
													</p>
													{/* Location */}
													<div className="flex items-center space-x-2">
														<MapPin className="h-4 w-4 text-gray-400" />
														<span className="text-sm text-gray-600 font-karla">
															{doula.location} • {doula.distance}
														</span>
													</div>
												</div>
											</div>
											<Button
												variant="ghost"
												size="sm"
												onClick={() => toggleFavorite(doula.id)}
												className={`p-2 ${doula.is_favorited ? "text-red-500" : "text-gray-400"}`}
											>
												<Heart
													className={`h-5 w-5 ${doula.is_favorited ? "fill-current" : ""}`}
												/>
											</Button>
										</div>
									</div>

									<CardContent className="space-y-4">
										{/* Experience */}
										<div className="flex items-center justify-end">
											<div className="text-sm text-gray-600 font-karla">
												{doula.experience_years} years experience
											</div>
										</div>

										{/* Bio */}
										<p className="text-sm text-gray-600 font-karla line-clamp-3">
											{doula.bio}
										</p>

										{/* Specialties */}
										<div className="flex flex-wrap gap-2">
											{doula.specialties.slice(0, 3).map((specialty, idx) => (
												<Badge
													key={idx}
													variant="secondary"
													className="text-xs"
												>
													{specialty}
												</Badge>
											))}
											{doula.specialties.length > 3 && (
												<Badge variant="outline" className="text-xs">
													+{doula.specialties.length - 3} more
												</Badge>
											)}
										</div>

										{/* Package Count and Action Buttons */}
										<div className="flex items-center justify-between pt-2">
											<div className="flex items-center space-x-2">
												<Package className="h-4 w-4 text-primary-600" />
												<span className="text-sm font-medium text-gray-900 font-karla">
													{doula.package_count} package
													{doula.package_count !== 1 ? "s" : ""} available
												</span>
											</div>
											<div className="flex space-x-2">
												<Button
													variant="outline"
													size="sm"
													onClick={() => handleViewProfile(doula.id)}
													className="text-xs shadow-sm"
												>
													View Profile
												</Button>
												<Button
													size="sm"
													onClick={() => handleBookAppointment(doula.id)}
													className="text-xs bg-primary-500 hover:bg-primary-600 shadow-sm"
												>
													<Calendar className="h-4 w-4 mr-1" />
													Book Intro Call
												</Button>
											</div>
										</div>
									</CardContent>
								</Card>
							</motion.div>
						))}
					</div>
				)}

				{/* Load More Button for Infinite Scroll */}
				{doulas.length > 0 && hasMore && (
					<div className="flex justify-center mt-8">
						<Button
							onClick={loadMoreDoulas}
							disabled={loadingMore}
							variant="outline"
							className="px-8 py-3"
						>
							{loadingMore ? (
								<div className="flex items-center">
									<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500 mr-2"></div>
									Loading more doulas...
								</div>
							) : (
								"Load More Doulas"
							)}
						</Button>
					</div>
				)}

				{doulas.length === 0 && !loading && (
					<div className="text-center py-12">
						<div className="text-gray-400 mb-4">
							<Search className="h-16 w-16 mx-auto" />
						</div>
						<h3 className="text-lg font-semibold text-gray-900 font-karla mb-2">
							No doulas found
						</h3>
						<p className="text-gray-600 font-karla">
							Try adjusting your search criteria or expanding your search
							radius.
						</p>
					</div>
				)}
			</div>

		</div>
	);
};

export default SearchDoulas;
